// https://regex101.com/r/ZKFfzC/3
import { getDemoUsernames } from '~/src/dash/resellers/utils/importResellerConfig';
import {
  FirstAndThirdPartyDashboardCardDataCompareKeys,
  FirstAndThirdPartyDashboardCardDataKeys,
  FirstAndThirdPartyDashboardCardDataTitles,
  IFirstAndThirdPartyDashboardCardDataDisplayItem,
} from '~/src/dash/views/measurement-home-view';
import {
  StreamDashboardCardDataCompareKeys,
  StreamDashboardCardDataKeys,
  StreamDashboardCardDataTitles,
} from '~/src/dash/views/networks/constants';

export const REGEX_EMAIL =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const REGEX_NUMERIC = /^[0-9]*$/;

export function onlyNumeric(s: string): boolean {
  return !!s && REGEX_NUMERIC.test(s);
}

export const DEMO_ACCOUNTS = getDemoUsernames();

export const APS_BIDDERS = ['apsOpenAuction', 'apsPMP', 'apsGuarantee'];
{
  /*  hide the toggle Podded Requests for Xandr aka appnexus (un-hide in q1 when xandr is compliant with 2.6.) */
}
export const BIDDERS_PODDED_REQUEST_ENABLED = [
  'appnexus',
  'beachfront',
  'column6',
  'connatix',
  'dynamicPlatformConnection',
  'elementaltv',
  'freewheelssp',
  'indexExchange',
  'magnitectv',
  'mobilefuse',
  'nexstar',
  'openx',
  'platformConnection',
  'pubmatic',
  'publica',
  'rhythmone',
  'samsung ssp test',
  'samsung ssp',
  'samsungssp',
  'samsungssptest',
  'smaato',
  'smartadserver',
  'smartclip',
  'sovrn',
  'teads',
  'thetradedesk',
  'triplelift',
  'vibe',
  'videoHeroesRevShare',
  'videoHeroesFixedCPM',
  'videobyte',
];

export const ORTB26_ENABLED_BIDDERS = [
  'appnexus',
  'column6',
  'connatix',
  'elementaltv',
  'freewheelssp',
  'indexExchange',
  'marketingarchitects',
  'mobilefuse',
  'nexstar',
  'openx',
  'pubmatic',
  'smartclip',
  'sovrn',
  'thetradedesk',
  'triplelift',
  'undertone',
  'vibe',
  'videobyte',
  'rhythmone',
  'teads',
] as string[];

export const ORTB26_ENABLED_PODDED_TYPE_DYNAMIC_BIDDERS = [
  'column6',
  'connatix',
  'elementaltv',
  'freewheelssp',
  'indexExchange',
  'mobilefuse',
  'nexstar',
  'openx',
  'pubmatic',
  'smartclip',
  'sovrn',
  'thetradedesk',
  'triplelift',
  'videobyte',
  'rhythmone',
  'teads',
] as string[];

export const ORTB26_ENABLED_PODDED_TYPE_STRUCTURED_BIDDERS = [
  'appnexus',
  'connatix',
  'elementaltv',
  'freewheelssp',
  'indexExchange',
  'mobilefuse',
  'openx',
  'pubmatic',
  'sovrn',
  'thetradedesk',
  'rhythmone',
  'triplelift',
  'teads',
  'vibe',
  'videobyte',
] as string[];

export const ORTB26_ENABLED_PODDED_TYPE_HYBRID_BIDDERS = [
  'connatix',
  'pubmatic',
  'rhythmone',
  'sovrn',
  'teads',
] as string[];

export const BIDDERS_IRIS_SIGNAL_ENABLED = [
  'spotx',
  'pubmatic',
  'beachfront',
  'indexExchange',
  'appnexus',
  'rhythmone',
  'platformConnection',
  'iponweb',
  'freewheel',
  'dynamicPlatformConnection',
  'videoHeroesRevShare',
  'videoHeroesFixedCPM',
  'magnitectv',
  'smartadserver',
  'videobyte',
];

export const BIDDERS_MULTI_BID_ENABLED = [
  'magnitectv',
  'spotx',
  'verizon', // for yahoo demand source in dropdown
];

export const BIDDERS_ADBREAK_TARGETING_ENABLED = ['publica'];

export const BIDDERS_WITHOUT_BID_ADJUSTMENT_FACTOR = [
  'samsung ssp test',
  'samsungssptest',
  'samsung ssp',
  'samsungssp',
];

export const ALL_PLATFORM_CONNECTION_BIDDERS = [
  'dynamicPlatformConnection',
  'platformConnection',
  'videoHeroesRevShare',
  'videoHeroesFixedCPM',
];

export const FIXED_PRICE_PLATFORM_CONNECTION_BIDDERS = [
  'platformConnection',
  'videoHeroesFixedCPM',
];

export const DYNAMIC_PLATFORM_CONNECTION_BIDDERS = [
  'dynamicPlatformConnection',
  'videoHeroesRevShare',
];

export const BIDDERS_SHARE_OF_VOICE_TYPE_ENABLED = [
  'publica',
  'platformConnection',
  'dynamicPlatformConnection',
  'videoHeroesRevShare',
  'videoHeroesFixedCPM',
];

// update DisabledSecondaryRequestBidders in platform with this array
export const NO_SECONDARY_REQUEST_BIDDERS = [
  'apsOpenAuction',
  'apsPMP',
  'apsGuarantee',
  'rokuHB',
  'publicaCampaign',
  'publica',
  'freewheel',
  'samsungssp',
  'platformConnection',
  'dynamicPlatformConnection',
];

// ---------------------- Home dashboard -----------------------------

export type HomeDashboardCardDataTitles =
  | 'Revenue'
  | 'Impressions'
  | 'CPM'
  | 'Endpoint Requests'
  | 'Render Rate'
  | 'Pod Fill Rate'
  | 'CPM Per Second'
  | 'Auctions'
  | 'Fill Rate'
  | 'Bid Requests'
  | 'Bid Responses'
  | 'Winning Bids'
  | 'Bid Response Rate'
  | 'Bid Timeout Rate'
  | 'Pod Clear Rate'
  | 'Paid Pod Clear Rate'
  | 'Endpoint Request eCPM';

export type IHomeDashboardCardDataDisplay = Record<
  HomeDashboardCardDataTitles,
  IHomeDashboardCardDataDisplayItem
>;

interface IHomeDashboardCardDataDisplayItem {
  value: string;
  sign: number;
}

// ---------------------- Network dashboard -----------------------------

// ---------------------- Measurement Dashboard -----------------------------
export type IMeasurementAggregateCardDataDisplay = Record<
  FirstAndThirdPartyDashboardCardDataTitles,
  IFirstAndThirdPartyDashboardCardDataDisplayItem
>;

// ---------------------- Dashboard Unions -----------------------------
export interface ISelectedMetric {
  key: DashboardCardDataTitles;
  active: boolean;
}
export type DashboardCardDataTitles =
  | StreamDashboardCardDataTitles
  | FirstAndThirdPartyDashboardCardDataTitles
  | HomeDashboardCardDataTitles;
export type DashboardCardDataKeys =
  | StreamDashboardCardDataKeys
  | FirstAndThirdPartyDashboardCardDataKeys;
export type DashboardCardDataCompareKeys =
  | StreamDashboardCardDataCompareKeys
  | FirstAndThirdPartyDashboardCardDataCompareKeys;

// ---------------------- Misc -----------------------------
export const CreativeTypeMediafile: string = 'mediafile';
export const CreativeTypeVast: string = 'vast';
export const WeightingTypeRequests: string = 'Requests count';
export const WeightingTypeImps: string = 'Imps count';

// ---------------------- Live Data ------------------------
export const DensityRangeHourByHour = 1;
export const DensityRange15Minutes = 15;
export const DensityRange30Minutes = 30;
export const DensityRange1HourByMinutes = 60;
export const DensityRange4HoursByMinutes = 240;
export const DensityRange4HoursByHour = 4;
export const DensityRange12HoursByHour = 12;
export const DensityRange24HoursByHour = 24;

export interface IDensityRange {
  text: string;
  value: number;
}
